import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ErrorApi,
  actualizarVisita,
  buscarProductos,
  crearVisita,
  obtenerCatalogos,
  obtenerMascota,
  obtenerVisita,
  subirAdjuntoDirecto,
  PropositoAdjunto,
  TipoItem,
  TipoProducto,
  type Catalogos,
  type GuardarVisitaItemRequest,
  type GuardarVisitaRequest,
  type MascotaDetalle,
  type ProductoDto,
  type VisitaDetalleDto,
} from "../api";
import Campo from "../componentes/Campo";
import BuscadorProducto from "../componentes/BuscadorProducto";
import GaleriaAdjuntos from "../componentes/GaleriaAdjuntos";
import { paraInputFecha } from "../formato";

type ItemFormulario = {
  idTemporal: string;
  tipo: TipoItem;
  productoID: number | null;
  productoNombre: string | null;
  nombreLibre: string | null;
  dosis: string | null;
  via: string | null;
  lote: string | null;
  numeroDosisEnSerie: number | null;
  notas: string | null;
};

const OPCIONES_TIPO_ITEM: { valor: TipoItem; nombre: string; icono: string; esProducto: boolean }[] = [
  { valor: TipoItem.Vacuna, nombre: "Vacuna", icono: "💉", esProducto: true },
  { valor: TipoItem.AntiparasitarioInterno, nombre: "Antiparasitario interno", icono: "🪱", esProducto: true },
  { valor: TipoItem.AntiparasitarioExterno, nombre: "Antiparasitario externo", icono: "🧴", esProducto: true },
  { valor: TipoItem.Medicamento, nombre: "Medicamento", icono: "💊", esProducto: true },
  { valor: TipoItem.Procedimiento, nombre: "Procedimiento", icono: "🩺", esProducto: false },
  { valor: TipoItem.Examen, nombre: "Examen", icono: "🔬", esProducto: false },
  { valor: TipoItem.Observacion, nombre: "Observación clínica", icono: "📝", esProducto: false },
];

function crearItemVacio(tipo: TipoItem = TipoItem.Vacuna): ItemFormulario {
  return {
    idTemporal: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    tipo,
    productoID: null,
    productoNombre: null,
    nombreLibre: null,
    dosis: null,
    via: null,
    lote: null,
    numeroDosisEnSerie: null,
    notas: null,
  };
}

function hoyIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function inferirTipoVisitaID(
  productoNombre: string,
  tipoProductoOItem: number | undefined,
  tiposVisita: { tipoVisitaID: number; nombre: string }[],
): number | null {
  const lower = productoNombre.toLowerCase();
  if (
    tipoProductoOItem === TipoItem.AntiparasitarioInterno ||
    tipoProductoOItem === TipoItem.AntiparasitarioExterno ||
    lower.includes("nexgard") ||
    lower.includes("bravecto") ||
    lower.includes("simparica") ||
    lower.includes("frontline") ||
    lower.includes("drontal") ||
    lower.includes("total") ||
    lower.includes("parasit") ||
    lower.includes("desparasit")
  ) {
    const tipoDesp = tiposVisita.find((tv) =>
      tv.nombre.toLowerCase().includes("desparasit"),
    );
    if (tipoDesp) return tipoDesp.tipoVisitaID;
  }
  if (
    tipoProductoOItem === TipoItem.Vacuna ||
    lower.includes("vacun") ||
    lower.includes("quintuple") ||
    lower.includes("octuple") ||
    lower.includes("sextuple") ||
    lower.includes("rabia") ||
    lower.includes("antirrabica") ||
    lower.includes("felocell") ||
    lower.includes("nobivac")
  ) {
    const tipoVac = tiposVisita.find((tv) =>
      tv.nombre.toLowerCase().includes("vacun"),
    );
    if (tipoVac) return tipoVac.tipoVisitaID;
  }
  return null;
}

export default function VisitaForm() {
  const { mascotaID, visitaID } = useParams();
  const [searchParams] = useSearchParams();
  const esEdicion = Boolean(visitaID);

  const paramProductoID = searchParams.get("productoID");
  const paramProductoNombre = searchParams.get("productoNombre");
  const paramTipo = searchParams.get("tipo");
  const paramMotivo = searchParams.get("motivo");

  const catalogos = useQuery({ queryKey: ["catalogos"], queryFn: obtenerCatalogos });

  const queryVisita = useQuery({
    queryKey: ["visita", visitaID],
    queryFn: () => obtenerVisita(visitaID!),
    enabled: esEdicion,
  });

  const targetMascotaID = mascotaID ?? queryVisita.data?.mascotaID;

  const queryMascota = useQuery({
    queryKey: ["mascota", targetMascotaID],
    queryFn: () => obtenerMascota(targetMascotaID!),
    enabled: Boolean(targetMascotaID),
  });

  if (
    catalogos.isPending ||
    (esEdicion && queryVisita.isPending) ||
    (!esEdicion && queryMascota.isPending)
  ) {
    return <p className="muted">Cargando formulario…</p>;
  }

  if (catalogos.error) return <p className="err">{catalogos.error.message}</p>;
  if (queryVisita.error) return <p className="err">{queryVisita.error.message}</p>;
  if (queryMascota.error) return <p className="err">{queryMascota.error.message}</p>;

  const mascota = queryMascota.data!;

  return (
    <FormularioVisita
      key={visitaID ?? `nueva-${paramProductoNombre ?? "visita"}`}
      esEdicion={esEdicion}
      visitaID={visitaID}
      mascota={mascota}
      catalogos={catalogos.data!}
      visitaExistente={queryVisita.data}
      paramProductoID={paramProductoID}
      paramProductoNombre={paramProductoNombre}
      paramTipo={paramTipo}
      paramMotivo={paramMotivo}
    />
  );
}

type PropsForm = {
  esEdicion: boolean;
  visitaID?: string;
  mascota: MascotaDetalle;
  catalogos: Catalogos;
  visitaExistente?: VisitaDetalleDto;
  paramProductoID?: string | null;
  paramProductoNombre?: string | null;
  paramTipo?: string | null;
  paramMotivo?: string | null;
};

function FormularioVisita({
  esEdicion,
  visitaID,
  mascota,
  catalogos,
  visitaExistente,
  paramProductoID,
  paramProductoNombre,
  paramTipo,
  paramMotivo,
}: PropsForm) {
  const navegar = useNavigate();
  const qc = useQueryClient();

  const [fecha, setFecha] = useState<string>(
    visitaExistente ? paraInputFecha(visitaExistente.fecha) : hoyIso(),
  );
  const [tipoVisitaID, setTipoVisitaID] = useState<number>(() => {
    if (visitaExistente) return visitaExistente.tipoVisitaID;
    if (paramProductoNombre) {
      const inferido = inferirTipoVisitaID(
        paramProductoNombre,
        paramTipo ? Number(paramTipo) : undefined,
        catalogos.tiposVisita,
      );
      if (inferido) return inferido;
    }
    return catalogos.tiposVisita[0]?.tipoVisitaID ?? 1;
  });
  const [pesoKg, setPesoKg] = useState<number | "">(
    visitaExistente?.pesoKg != null ? visitaExistente.pesoKg : "",
  );
  const [motivo, setMotivo] = useState<string>(() => {
    if (visitaExistente?.motivo) return visitaExistente.motivo;
    if (paramMotivo) return paramMotivo;
    if (paramProductoNombre) return `Aplicación de ${paramProductoNombre}`;
    return "";
  });
  const [diagnostico, setDiagnostico] = useState<string>(visitaExistente?.diagnostico ?? "");
  const [notas, setNotas] = useState<string>(visitaExistente?.notas ?? "");

  const [sugerenciaAplicada, setSugerenciaAplicada] = useState<string | null>(() => {
    return paramProductoNombre || null;
  });

  const aplicarSugerenciaProducto = (prod: { nombre: string; tipo?: number }) => {
    setSugerenciaAplicada(prod.nombre);
    if (!motivo.trim() || motivo.startsWith("Aplicación de ") || motivo.startsWith("Control de ")) {
      setMotivo(`Aplicación de ${prod.nombre}`);
    }
    const nuevoTipoVisitaID = inferirTipoVisitaID(prod.nombre, prod.tipo, catalogos.tiposVisita);
    if (nuevoTipoVisitaID) {
      setTipoVisitaID(nuevoTipoVisitaID);
    }
  };

  // Entrada rápida por medicamento o vacuna
  const [busquedaRapida, setBusquedaRapida] = useState("");
  const [abiertoRapido, setAbiertoRapido] = useState(false);
  const contenedorRapidoRef = useRef<HTMLDivElement>(null);

  const queryBusquedaRapida = useQuery({
    queryKey: ["productos-rapidos", { q: busquedaRapida, especieID: mascota.especieID }],
    queryFn: () => buscarProductos({ q: busquedaRapida, especieID: mascota.especieID }),
    enabled: abiertoRapido && busquedaRapida.trim().length >= 1,
  });

  useEffect(() => {
    function clickFuera(e: MouseEvent) {
      if (contenedorRapidoRef.current && !contenedorRapidoRef.current.contains(e.target as Node)) {
        setAbiertoRapido(false);
      }
    }
    document.addEventListener("mousedown", clickFuera);
    return () => document.removeEventListener("mousedown", clickFuera);
  }, []);

  const seleccionarProductoRapido = (prod: ProductoDto) => {
    const nuevoItem: ItemFormulario = {
      idTemporal: `item-rapido-${Date.now()}`,
      tipo: prod.tipo as unknown as TipoItem,
      productoID: prod.productoID,
      productoNombre: prod.nombre,
      nombreLibre: null,
      dosis: null,
      via: null,
      lote: null,
      numeroDosisEnSerie: null,
      notas: null,
    };
    setItems([nuevoItem]);
    aplicarSugerenciaProducto(prod);
    setBusquedaRapida("");
    setAbiertoRapido(false);
  };

  const [items, setItems] = useState<ItemFormulario[]>(() => {
    if (visitaExistente && visitaExistente.items.length > 0) {
      return visitaExistente.items.map((i) => ({
        idTemporal: `item-${i.visitaItemID}`,
        tipo: i.tipo,
        productoID: i.productoID,
        productoNombre: i.productoNombre,
        nombreLibre: i.nombreLibre,
        dosis: i.dosis,
        via: i.via,
        lote: i.lote,
        numeroDosisEnSerie: i.numeroDosisEnSerie,
        notas: i.notas,
      }));
    }

    if (paramProductoNombre) {
      const tipoNumero = paramTipo ? Number(paramTipo) : null;
      let tipoDetectado: TipoItem = TipoItem.Vacuna;
      if (tipoNumero && [1, 2, 3, 4].includes(tipoNumero)) {
        tipoDetectado = tipoNumero as TipoItem;
      } else {
        const lower = paramProductoNombre.toLowerCase();
        if (
          lower.includes("nexgard") ||
          lower.includes("bravecto") ||
          lower.includes("simparica") ||
          lower.includes("frontline") ||
          lower.includes("extern")
        ) {
          tipoDetectado = TipoItem.AntiparasitarioExterno;
        } else if (
          lower.includes("drontal") ||
          lower.includes("total") ||
          lower.includes("intern")
        ) {
          tipoDetectado = TipoItem.AntiparasitarioInterno;
        }
      }

      return [
        {
          idTemporal: `item-inicial`,
          tipo: tipoDetectado,
          productoID: paramProductoID ? Number(paramProductoID) : null,
          productoNombre: paramProductoNombre,
          nombreLibre: null,
          dosis: null,
          via: null,
          lote: null,
          numeroDosisEnSerie: null,
          notas: null,
        },
      ];
    }

    return [];
  });

  // Auto-asociar el ProductoID desde el catálogo si sólo se proveyó el nombre
  useEffect(() => {
    if (!esEdicion && paramProductoNombre) {
      buscarProductos({ q: paramProductoNombre, especieID: mascota.especieID })
        .then((prods) => {
          const match =
            prods.find((p) => p.nombre.toLowerCase() === paramProductoNombre.toLowerCase()) ??
            prods[0];
          if (match) {
            setItems((actuales) => {
              if (actuales.length === 0) return actuales;
              const primer = actuales[0];
              if (!primer || primer.productoID) return actuales;
              const itemActualizado: ItemFormulario = {
                idTemporal: primer.idTemporal,
                tipo: match.tipo as TipoItem,
                productoID: match.productoID,
                productoNombre: match.nombre,
                nombreLibre: primer.nombreLibre,
                dosis: primer.dosis,
                via: primer.via,
                lote: primer.lote,
                numeroDosisEnSerie: primer.numeroDosisEnSerie,
                notas: primer.notas,
              };
              return [itemActualizado, ...actuales.slice(1)];
            });
          }
        })
        .catch(() => {});
    }
  }, [esEdicion, paramProductoNombre, mascota.especieID]);

  const [errores, setErrores] = useState<Record<string, string[]>>({});
  const [archivosNuevos, setArchivosNuevos] = useState<File[]>([]);
  const [subiendoArchivos, setSubiendoArchivos] = useState(false);

  const guardar = useMutation({
    mutationFn: async (solicitud: GuardarVisitaRequest) => {
      const res = esEdicion
        ? await actualizarVisita(visitaID!, solicitud).then(() => ({ visitaID: visitaID! }))
        : await crearVisita(solicitud);

      if (archivosNuevos.length > 0) {
        setSubiendoArchivos(true);
        for (const arch of archivosNuevos) {
          const esImg = arch.type.startsWith("image/");
          await subirAdjuntoDirecto(arch, {
            mascotaID: mascota.mascotaID,
            visitaID: res.visitaID,
            proposito: esImg ? PropositoAdjunto.FotoPerfil : PropositoAdjunto.Otro,
          });
        }
      }
      return res;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["visitas", mascota.mascotaID] });
      qc.invalidateQueries({ queryKey: ["adjuntos", mascota.mascotaID] });
      if (res?.visitaID) qc.invalidateQueries({ queryKey: ["visita", res.visitaID] });
      qc.invalidateQueries({ queryKey: ["mascota", mascota.mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      qc.invalidateQueries({ queryKey: ["recordatorios", mascota.mascotaID] });
      navegar(`/mascotas/${mascota.mascotaID}`);
    },
    onError: (e) => {
      setSubiendoArchivos(false);
      if (e instanceof ErrorApi) {
        setErrores(e.errores);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrores({});

    if (
      items.length === 0 &&
      !motivo.trim() &&
      !diagnostico.trim() &&
      !notas.trim() &&
      (pesoKg === "" || pesoKg === null)
    ) {
      setErrores({
        items: [
          "Debes agregar al menos un ítem o registrar motivo, diagnóstico o notas de la atención.",
        ],
      });
      return;
    }

    const itemsRequest: GuardarVisitaItemRequest[] = items.map((i) => ({
      tipo: i.tipo,
      productoID: i.productoID,
      nombreLibre: i.nombreLibre,
      dosis: i.dosis?.trim() || null,
      via: i.via?.trim() || null,
      lote: i.lote?.trim() || null,
      numeroDosisEnSerie: i.numeroDosisEnSerie != null && i.numeroDosisEnSerie > 0 ? i.numeroDosisEnSerie : null,
      notas: i.notas?.trim() || null,
    }));

    const solicitud: GuardarVisitaRequest = {
      mascotaID: mascota.mascotaID,
      fecha,
      tipoVisitaID,
      clinicaID: visitaExistente?.clinicaID ?? null,
      profesionalUsuarioID: visitaExistente?.profesionalUsuarioID ?? null,
      motivo: motivo.trim() || null,
      diagnostico: diagnostico.trim() || null,
      notas: notas.trim() || null,
      pesoKg: typeof pesoKg === "number" && pesoKg > 0 ? pesoKg : null,
      items: itemsRequest,
    };

    guardar.mutate(solicitud);
  };

  const agregarItem = (tipo: TipoItem) => {
    setItems((prev) => [...prev, crearItemVacio(tipo)]);
  };

  const eliminarItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const moverItem = (index: number, direccion: "arriba" | "abajo") => {
    setItems((prev) => {
      const nuevo = [...prev];
      const targetIndex = direccion === "arriba" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= nuevo.length) return prev;
      const temp = nuevo[index]!;
      nuevo[index] = nuevo[targetIndex]!;
      nuevo[targetIndex] = temp;
      return nuevo;
    });
  };

  const actualizarItem = (index: number, cambios: Partial<ItemFormulario>) => {
    setItems((prev) => {
      const copia = [...prev];
      copia[index] = { ...copia[index]!, ...cambios };
      return copia;
    });
  };

  return (
    <>
      <p className="migas">
        <Link to={`/mascotas/${mascota.mascotaID}`}>← Volver a {mascota.nombre}</Link>
      </p>

      <div className="titulo-fila">
        <h1>{esEdicion ? "Editar visita" : `Registrar atención para ${mascota.nombre}`}</h1>
      </div>

      <form className="formulario" onSubmit={handleSubmit}>
        {/* Aviso de sugerencia inteligente */}
        {sugerenciaAplicada && (
          <div className="entrada-rapida-aviso-auto">
            <span>
              ⚡ Se autocompletó el tipo de visita y motivo para <strong>{sugerenciaAplicada}</strong>.
            </span>
            <button
              type="button"
              className="boton-chico"
              onClick={() => setSugerenciaAplicada(null)}
              title="Ocultar aviso"
              style={{ padding: "0.15rem 0.4rem", fontSize: "0.75rem" }}
            >
              Entendido
            </button>
          </div>
        )}

        {/* Entrada Rápida por Medicamento o Vacuna */}
        {!esEdicion && items.length === 0 && (
          <div className="panel panel-entrada-rapida" ref={contenedorRapidoRef}>
            <div className="entrada-rapida-cabecera">
              <span style={{ fontSize: "1.2rem" }} aria-hidden="true">
                ⚡
              </span>
              <h2 className="entrada-rapida-titulo">Entrada rápida por medicamento o vacuna</h2>
            </div>
            <p className="entrada-rapida-desc">
              Si la atención fue principalmente para aplicar un producto (vacuna, antiparasitario o medicamento),
              escríbelo aquí y pre-llenaremos el tipo de visita, motivo e ítem automáticamente:
            </p>
            <div className="entrada-rapida-input-wrap">
              <input
                type="text"
                className="entrada-rapida-input"
                placeholder="Buscar producto (ej: Nexgard, Bravecto, Nobivac, Drontal, Simparica…)"
                value={busquedaRapida}
                onChange={(e) => {
                  setBusquedaRapida(e.target.value);
                  setAbiertoRapido(true);
                }}
                onFocus={() => {
                  if (busquedaRapida.trim().length > 0) setAbiertoRapido(true);
                }}
              />
              {abiertoRapido && busquedaRapida.trim().length >= 1 && (
                <ul className="entrada-rapida-dropdown">
                  {queryBusquedaRapida.isPending && (
                    <li className="entrada-rapida-opcion muted">Buscando productos…</li>
                  )}
                  {queryBusquedaRapida.data && queryBusquedaRapida.data.length === 0 && (
                    <li className="entrada-rapida-opcion muted">No se encontraron productos coincidentes.</li>
                  )}
                  {queryBusquedaRapida.data?.map((prod) => (
                    <li
                      key={prod.productoID}
                      className="entrada-rapida-opcion"
                      onClick={() => seleccionarProductoRapido(prod)}
                    >
                      <span>
                        <strong>{prod.nombre}</strong>
                        {prod.laboratorio && (
                          <span className="muted" style={{ marginLeft: "0.4rem" }}>
                            ({prod.laboratorio})
                          </span>
                        )}
                      </span>
                      <span className="etiqueta neutra">
                        {prod.tipo === TipoProducto.Vacuna
                          ? "Vacuna"
                          : prod.tipo === TipoProducto.AntiparasitarioInterno
                            ? "Antiparasitario interno"
                            : prod.tipo === TipoProducto.AntiparasitarioExterno
                              ? "Antiparasitario externo"
                              : "Medicamento"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Cabecera del encuentro */}
        <section className="panel">
          <h2>Datos del encuentro</h2>
          <div className="fila">
            <Campo etiqueta="Fecha de atención" errores={errores.fecha} requerido>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                required
              />
            </Campo>

            <Campo etiqueta="Tipo de visita" errores={errores.tipoVisitaID} requerido>
              <select
                value={tipoVisitaID}
                onChange={(e) => setTipoVisitaID(Number(e.target.value))}
              >
                {catalogos.tiposVisita.map((tv) => (
                  <option key={tv.tipoVisitaID} value={tv.tipoVisitaID}>
                    {tv.nombre}
                  </option>
                ))}
              </select>
            </Campo>

            <Campo
              etiqueta="Peso actual (kg)"
              errores={errores.pesoKg}
              ayuda="Opcional. Registra el peso medido hoy."
            >
              <input
                type="number"
                step="0.01"
                min="0.1"
                max="1000"
                placeholder="Ej: 8.5"
                value={pesoKg}
                onChange={(e) => setPesoKg(e.target.value ? Number(e.target.value) : "")}
              />
            </Campo>
          </div>

          <div className="fila" style={{ marginTop: "0.75rem" }}>
            <Campo etiqueta="Motivo de consulta" errores={errores.motivo}>
              <input
                type="text"
                placeholder="Ej: Control anual, evaluación física, decaimiento…"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </Campo>
          </div>

          <div className="fila" style={{ marginTop: "0.75rem" }}>
            <Campo etiqueta="Diagnóstico / Evaluación" errores={errores.diagnostico}>
              <textarea
                rows={2}
                placeholder="Ej: Mascota en buen estado general, mucosas rosadas…"
                value={diagnostico}
                onChange={(e) => setDiagnostico(e.target.value)}
              />
            </Campo>
          </div>

          <div className="fila" style={{ marginTop: "0.75rem" }}>
            <Campo etiqueta="Indicaciones y notas generales" errores={errores.notas}>
              <textarea
                rows={2}
                placeholder="Ej: Reposo 24 horas, control en 6 meses…"
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
              />
            </Campo>
          </div>
        </section>

        {/* Sección de Ítems administrados */}
        <section className="panel">
          <div className="titulo-fila">
            <div>
              <h2>Ítems administrados o realizados ({items.length})</h2>
              <p className="muted" style={{ margin: 0, fontSize: "0.875rem" }}>
                Opcional. Agrega las vacunas, antiparasitarios, medicamentos o procedimientos realizados hoy.
              </p>
            </div>
          </div>

          {errores.items && <p className="err" style={{ marginTop: "0.5rem" }}>{errores.items[0]}</p>}

          <div className="items-lista" style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
            {items.length === 0 && (
              <p className="muted" style={{ padding: "0.75rem 0", margin: 0 }}>
                No has agregado medicamentos ni procedimientos a esta atención. Si solo realizaste una consulta general, evaluación física o control de peso, puedes guardar directamente.
              </p>
            )}

            {items.map((item, index) => {
              const tipoConfig = OPCIONES_TIPO_ITEM.find((t) => t.valor === item.tipo)!;
              const errorItem = errores[`items[${index}]`];
              const errorNombre = errores[`items[${index}].nombreLibre`];
              const errorDosis = errores[`items[${index}].dosis`];
              const errorVia = errores[`items[${index}].via`];
              const errorLote = errores[`items[${index}].lote`];
              const errorNotas = errores[`items[${index}].notas`];

              return (
                <div key={item.idTemporal} className="panel tarjeta-item-visita">
                  <div className="item-visita-cabecera">
                    <div className="item-visita-titulo">
                      <span className="item-visita-numero">#{index + 1}</span>
                      <select
                        className="item-visita-tipo-select"
                        value={item.tipo}
                        onChange={(e) => {
                          const nuevoTipo = Number(e.target.value) as TipoItem;
                          actualizarItem(index, {
                            tipo: nuevoTipo,
                            productoID: null,
                            productoNombre: null,
                            nombreLibre: null,
                            dosis: null,
                            via: null,
                            lote: null,
                            numeroDosisEnSerie: null,
                          });
                        }}
                      >
                        {OPCIONES_TIPO_ITEM.map((op) => (
                          <option key={op.valor} value={op.valor}>
                            {op.icono} {op.nombre}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="item-visita-acciones">
                      <button
                        type="button"
                        className="boton-icono"
                        onClick={() => moverItem(index, "arriba")}
                        disabled={index === 0}
                        title="Mover arriba"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="boton-icono"
                        onClick={() => moverItem(index, "abajo")}
                        disabled={index === items.length - 1}
                        title="Mover abajo"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="boton-peligro boton-icono"
                        onClick={() => eliminarItem(index)}
                        title="Quitar este ítem"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {errorItem && <p className="err">{errorItem[0]}</p>}

                  {/* Cuerpo del ítem */}
                  <div className="item-visita-cuerpo">
                    {tipoConfig.esProducto ? (
                      <BuscadorProducto
                        tipo={item.tipo as unknown as TipoProducto}
                        especieID={mascota.especieID}
                        productoID={item.productoID}
                        productoNombre={item.productoNombre}
                        nombreLibre={item.nombreLibre}
                        errores={errorNombre}
                        requerido
                        onSeleccionarProducto={(prod) => {
                          actualizarItem(index, {
                            productoID: prod?.productoID ?? null,
                            productoNombre: prod?.nombre ?? null,
                            nombreLibre: null,
                          });
                          if (prod) {
                            aplicarSugerenciaProducto(prod);
                          }
                        }}
                        onCambiarNombreLibre={(texto) => {
                          actualizarItem(index, {
                            productoID: null,
                            productoNombre: null,
                            nombreLibre: texto || null,
                          });
                        }}
                      />
                    ) : (
                      <Campo
                        etiqueta={`Descripción del ${tipoConfig.nombre.toLowerCase()}`}
                        errores={errorNombre}
                        requerido
                      >
                        <input
                          type="text"
                          placeholder={`Ej: ${
                            item.tipo === TipoItem.Procedimiento
                              ? "Corte de uñas, Limpieza de oídos, Curación…"
                              : item.tipo === TipoItem.Examen
                                ? "Perfil bioquímico, Radiografía, Hemograma…"
                                : "Observación sobre comportamiento, dieta o evolución…"
                          }`}
                          value={item.nombreLibre ?? ""}
                          onChange={(e) =>
                            actualizarItem(index, { nombreLibre: e.target.value || null })
                          }
                          required
                        />
                      </Campo>
                    )}

                    {/* Campos farmacológicos solo para vacunas, antiparasitarios y medicamentos */}
                    {tipoConfig.esProducto && (
                      <div className="fila" style={{ marginTop: "0.5rem" }}>
                        <Campo etiqueta="Dosis" errores={errorDosis}>
                          <input
                            type="text"
                            placeholder="Ej: 1 dosis, 2.5 ml, 1 comprimido"
                            value={item.dosis ?? ""}
                            onChange={(e) =>
                              actualizarItem(index, { dosis: e.target.value || null })
                            }
                          />
                        </Campo>

                        <Campo etiqueta="Vía de administración" errores={errorVia}>
                          <input
                            type="text"
                            placeholder="Ej: Subcutánea, Oral, Tópica"
                            value={item.via ?? ""}
                            onChange={(e) =>
                              actualizarItem(index, { via: e.target.value || null })
                            }
                          />
                        </Campo>

                        {item.tipo === TipoItem.Vacuna && (
                          <>
                            <Campo etiqueta="Lote" errores={errorLote} ayuda="Opcional">
                              <input
                                type="text"
                                placeholder="Ej: LOT-98234"
                                value={item.lote ?? ""}
                                onChange={(e) =>
                                  actualizarItem(index, { lote: e.target.value || null })
                                }
                              />
                            </Campo>

                            <Campo
                              etiqueta="Dosis en serie"
                              ayuda="Ej: 1, 2 ó 3 en cachorros"
                            >
                              <input
                                type="number"
                                min="1"
                                max="10"
                                placeholder="Ej: 1"
                                value={item.numeroDosisEnSerie ?? ""}
                                onChange={(e) =>
                                  actualizarItem(index, {
                                    numeroDosisEnSerie: e.target.value ? Number(e.target.value) : null,
                                  })
                                }
                              />
                            </Campo>
                          </>
                        )}
                      </div>
                    )}

                    <div style={{ marginTop: "0.5rem" }}>
                      <Campo etiqueta="Notas del ítem" errores={errorNotas}>
                        <input
                          type="text"
                          placeholder={
                            tipoConfig.esProducto
                              ? "Notas específicas sobre este producto o reacción observada"
                              : item.tipo === TipoItem.Examen
                                ? "Hallazgos preliminares o indicaciones de toma de muestra"
                                : "Observaciones o detalles adicionales"
                          }
                          value={item.notas ?? ""}
                          onChange={(e) =>
                            actualizarItem(index, { notas: e.target.value || null })
                          }
                        />
                      </Campo>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Botones para agregar items */}
          <div className="agregar-items-barra" style={{ marginTop: "1.25rem" }}>
            <span className="muted" style={{ fontSize: "0.875rem", marginRight: "0.5rem" }}>
              Agregar a la visita:
            </span>
            <div className="acciones" style={{ display: "inline-flex", flexWrap: "wrap", gap: "0.4rem" }}>
              <button
                type="button"
                className="boton"
                onClick={() => agregarItem(TipoItem.Vacuna)}
              >
                💉 + Vacuna
              </button>
              <button
                type="button"
                className="boton"
                onClick={() => agregarItem(TipoItem.AntiparasitarioInterno)}
              >
                🪱 + Antiparasitario
              </button>
              <button
                type="button"
                className="boton"
                onClick={() => agregarItem(TipoItem.Medicamento)}
              >
                💊 + Medicamento
              </button>
              <button
                type="button"
                className="boton"
                onClick={() => agregarItem(TipoItem.Procedimiento)}
              >
                🩺 + Procedimiento
              </button>
              <button
                type="button"
                className="boton"
                onClick={() => agregarItem(TipoItem.Examen)}
              >
                🔬 + Examen
              </button>
              <button
                type="button"
                className="boton"
                onClick={() => agregarItem(TipoItem.Observacion)}
              >
                📝 + Observación
              </button>
            </div>
          </div>
        </section>

        {/* Fotos y documentos de la visita */}
        <section className="panel">
          <div className="titulo-fila">
            <div>
              <h2>Fotos y documentos adjuntos</h2>
              <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                Puedes adjuntar fotos de la consulta, heridas, ecografías o informes PDF asociados a esta atención.
              </p>
            </div>
          </div>

          <div style={{ marginTop: "0.75rem" }}>
            <label
              className="boton boton-chico"
              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
            >
              <span>📷 Agregar fotos / documentos</span>
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    const nuevos = Array.from(e.target.files);
                    setArchivosNuevos((prev) => [...prev, ...nuevos]);
                  }
                }}
              />
            </label>
            <span className="muted" style={{ fontSize: "0.8rem", marginLeft: "0.75rem" }}>
              Formatos aceptados: JPG, PNG, WEBP, GIF, PDF
            </span>
          </div>

          {archivosNuevos.length > 0 && (
            <div style={{ marginTop: "1rem" }}>
              <p style={{ fontSize: "0.85rem", fontWeight: 600, margin: "0 0 0.5rem" }}>
                Archivos nuevos por subir ({archivosNuevos.length}):
              </p>
              <div className="mini-galeria-adjuntos">
                {archivosNuevos.map((arch, idx) => {
                  const esImg = arch.type.startsWith("image/");
                  const urlPreview = esImg ? URL.createObjectURL(arch) : null;
                  return (
                    <div key={`${arch.name}-${idx}`} className="mini-adjunto-tarjeta">
                      {esImg && urlPreview ? (
                        <img src={urlPreview} alt={arch.name} className="mini-adjunto-img" />
                      ) : (
                        <div className="mini-adjunto-doc">
                          <span>📄</span>
                          <small>PDF</small>
                        </div>
                      )}
                      <div className="mini-adjunto-meta">
                        <span
                          title={arch.name}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "70px",
                            fontSize: "0.75rem",
                          }}
                        >
                          {arch.name}
                        </span>
                        <button
                          type="button"
                          className="boton-chico"
                          style={{ padding: "0 4px", fontSize: "0.75rem", color: "var(--err)" }}
                          onClick={() =>
                            setArchivosNuevos((prev) => prev.filter((_, i) => i !== idx))
                          }
                          title="Descartar archivo"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {esEdicion && visitaID && (
            <div style={{ marginTop: "1.5rem", borderTop: "1px dashed var(--rule)", paddingTop: "1rem" }}>
              <h3 style={{ fontSize: "0.9rem", color: "var(--ink-2)", marginBottom: "0.5rem" }}>
                Archivos ya guardados en esta atención:
              </h3>
              <GaleriaAdjuntos
                mascotaID={mascota.mascotaID}
                visitaID={visitaID}
                puedeEditar={mascota.puedeEditar}
              />
            </div>
          )}
        </section>

        {guardar.error && Object.keys(errores).length === 0 && (
          <p className="err">{guardar.error.message}</p>
        )}

        <div className="acciones">
          <button
            type="submit"
            className="boton-primario"
            disabled={guardar.isPending || subiendoArchivos}
          >
            {subiendoArchivos
              ? `Subiendo archivos (${archivosNuevos.length})…`
              : guardar.isPending
                ? "Guardando visita…"
                : "Guardar visita"}
          </button>
          <Link className="boton" to={`/mascotas/${mascota.mascotaID}`}>
            Cancelar
          </Link>
        </div>
      </form>
    </>
  );
}
